import { BaseFilterButton } from 'components/base-table/filters/base-filter-button';
import { Button, FormControl, FormLabel, Input, Stack, useDisclosure } from 'design-system';
import { usePageParams } from 'hooks/use-page-params';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';

type TInputs = {
  universalName?: string;
};

export const UniversalNameFilterButton = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { getPageParams, setPageParams } = usePageParams();

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<TInputs>();

  const { universalName } = getPageParams();

  useEffect(() => {
    if (isOpen) {
      reset({ universalName: universalName ? String(universalName) : '' });
    }
  }, [isOpen, universalName, reset]);

  const onSubmit = useCallback(
    (data: TInputs) => {
      const newParams = { ...getPageParams() };
      if (data.universalName?.trim()) {
        newParams.universalName = data.universalName.trim();
      } else {
        delete newParams.universalName;
      }
      setPageParams(newParams);
      onClose();
    },
    [setPageParams, getPageParams, onClose]
  );

  const onClear = useCallback(() => {
    const newParams = { ...getPageParams() };
    delete newParams.universalName;
    setPageParams(newParams);
    reset();
    onClose();
  }, [getPageParams, setPageParams, reset, onClose]);

  const hasFilters = !!universalName;

  const handleFormSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      e.stopPropagation();
      handleSubmit(onSubmit)(e);
    },
    [handleSubmit, onSubmit]
  );

  return (
    <BaseFilterButton
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      isActive={hasFilters}
      formComponent={
        <Stack as='form' spacing='lg' p='md' pt='lg' onSubmit={handleFormSubmit} onClick={(e) => e.stopPropagation()}>
          <FormControl>
            <FormLabel>Universal Name</FormLabel>
            <Input size='sm' placeholder='Search by universal name...' {...register('universalName')} />
          </FormControl>

          <Stack direction='row' justify='flex-end' spacing={2}>
            <Button variant='outline' colorScheme='red' onClick={onClear}>
              Clear
            </Button>
            <Button variant='outline' colorScheme='gray' onClick={onClose}>
              Cancel
            </Button>
            <Button variant='solid' colorScheme='blue' type='submit' isLoading={isSubmitting}>
              Filter
            </Button>
          </Stack>
        </Stack>
      }
    />
  );
};
